import { meSelector } from '../../redux/app/selectors.ts';
import { getStore } from '../../redux/store/getStore.ts';
import type { PushNotification } from '../../types/PushNotification.d.ts';

type PushNotificationCallback = (notification: PushNotification) => void;

export class PushNotifications {
  /** The chartCheckId we are connected as. */
  private connectedAs?:string;

  /** Listeners for specific notification types. */
  private listeners: { type: string, callback: PushNotificationCallback }[] = [];

  /** Subscriptions which receive all notifications. */
  private subscriptions: PushNotificationCallback[] = [];

  /** The actual connection */
  private eventSource?: EventSource;

  private unsubscribeStore?: () => void;

  isConnected() {
    return this.eventSource?.readyState === EventSource.OPEN;
  }

  /**
   * Connects to the server.
   * Automatically reconnect or disconnects if the user's credentials change (they log in/out)
   */
  connect() {
    const me = meSelector(getStore().getState());

    this.eventSource = new EventSource('/weekly-check/api/push');
    this.eventSource.addEventListener('message', this.handleMessage);

    this.unsubscribeStore = getStore().subscribe(this.handleStateChange);

    this.connectedAs = me?.chartCheckId;
  }

  /** Disconnects from the server. */
  disconnect() {
    this.eventSource?.close();
    this.eventSource = undefined;

    this.unsubscribeStore?.();

    this.connectedAs = undefined;
  }

  /** Listen for a specific notification type. */
  addListener(type: string, callback: PushNotificationCallback) {
    this.listeners.push({ type, callback });
  }

  /** Removes a listener. */
  removeListener(type: string, callback: PushNotificationCallback) {
    const index = this.listeners.findIndex(subscription =>
      subscription.type === type && subscription.callback === callback);

    if (index === -1) return;

    this.listeners.splice(index, 1);
  }

  /** Subscribes to all messages. */
  subscribe(callback: PushNotificationCallback) {
    this.subscriptions.push(callback);
  }

  /** Removes a specific subscribe() callback. */
  unsubscribe(callback: PushNotificationCallback) {
    const index = this.subscriptions.indexOf(callback);

    if (index === -1) return;

    this.subscriptions.splice(index, 1);
  }

  private handleStateChange = () => {
    const me = meSelector(getStore().getState());

    if (this.eventSource && me?.chartCheckId !== this.connectedAs) {
      this.disconnect();
      this.connect();
    }
  };

  private handleMessage = (event: MessageEvent) => {
    const data = JSON.parse(event.data);
    const { type } = data;

    const callbacks = [
      ...this.listeners.filter(s => s.type === type)
        .map(({ callback }) => callback),
      ...this.subscriptions
    ];

    for (const callback of callbacks) {
      callback(data);
    }
  };
}

export const pushNotifications = new PushNotifications();